import {
  type ColumnDef,
  type ColumnVisibilityState,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Columns3, RotateCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CopyButton } from "~/components/copy-button";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { EmptyState } from "~/components/empty-state";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui";
import { useCompoundRecordVersionList } from "~/features/compoundRecordVersions/hooks";
import type {
  CompoundRecordVersionRow,
  CompoundRecordVersionSearchCondition,
  CompoundRecordVersionSortField,
  SortDirection,
} from "~/features/compoundRecordVersions/types";

const SORTABLE: ReadonlySet<string> = new Set<CompoundRecordVersionSortField>(["id", "version", "createdAt"]);
const DEFAULT_SORT: CompoundRecordVersionSortField = "createdAt";

const VERSION_COLUMNS = [
  { id: "id", labelKey: "model.common.id" },
  { id: "version", labelKey: "ui.version" },
  { id: "createdAt", labelKey: "model.common.createdAt" },
] as const;

interface CompoundRecordVersionsTableProps {
  searchConditions: readonly CompoundRecordVersionSearchCondition[];
  sortField: CompoundRecordVersionSortField;
  direction: SortDirection;
  onSortingChange: (field: CompoundRecordVersionSortField, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
}

const helper = createColumnHelper<DataTableFeatures, CompoundRecordVersionRow>();

export function CompoundRecordVersionsTable({
  searchConditions,
  sortField,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
}: CompoundRecordVersionsTableProps) {
  const { t } = useTranslation();
  const {
    rows: rawRows,
    rowCount,
    hasNextPage,
    refetch,
  } = useCompoundRecordVersionList({
    conditions: searchConditions,
    sortField,
    direction,
    pageIndex,
    pageSize,
  });
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});

  const sorting: SortingState = useMemo(() => [{ id: sortField, desc: direction === "desc" }], [sortField, direction]);

  const handleSortingChange = (next: SortingState) => {
    const first = next[0];
    if (!first || !SORTABLE.has(first.id)) {
      onSortingChange(DEFAULT_SORT, "desc");
      return;
    }
    onSortingChange(first.id as CompoundRecordVersionSortField, first.desc ? "desc" : "asc");
  };

  const rows = useMemo<CompoundRecordVersionRow[]>(
    () => rawRows.map((v) => ({ id: v.id, version: v.version, createdAt: v.createdAt })),
    [rawRows],
  );

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) =>
    setColumnVisibility(Object.fromEntries(VERSION_COLUMNS.map((c) => [c.id, next])));

  const columns: ColumnDef<DataTableFeatures, CompoundRecordVersionRow, any>[] = [
    helper.accessor("id", {
      id: "id",
      header: () => t("model.common.id"),
      cell: (info) => (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums text-fg-2">
          {info.getValue()}
          <CopyButton value={info.getValue()} label={t("ui.copyId")} successMessageKey="feature.common.idCopied" />
        </span>
      ),
    }),
    helper.accessor("version", {
      id: "version",
      header: () => t("ui.version"),
      cell: (info) => <span className="whitespace-nowrap font-medium tabular-nums text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("createdAt", {
      id: "createdAt",
      header: () => t("model.common.createdAt"),
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {value ? t("date.long", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
  ];

  const isEmpty = rows.length === 0 && !isPending;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
              <Columns3 />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            {VERSION_COLUMNS.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.id}
                checked={isVisible(col.id)}
                onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(col.labelKey)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" size="icon" onClick={() => void refetch()} aria-label={t("ui.reload")}>
          <RotateCw />
        </Button>
      </div>

      {isEmpty ? (
        <EmptyState
          title={t("feature.compoundRecordVersions.empty.title")}
          description={t("feature.compoundRecordVersions.empty.description")}
        />
      ) : (
        <RemoteDataTable<CompoundRecordVersionRow>
          columns={columns}
          data={rows}
          rowCount={rowCount}
          hasNextPage={hasNextPage}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          isPending={isPending}
          emptyMessage={t("feature.common.noData")}
        />
      )}
    </div>
  );
}
